import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "store";
import { GameCenterFormTypes } from "configs/types";
import { closeModal } from "store/modals/slice";
import {
  createMultiScope,
  getMultiScopeComposition,
  getMultiScopeItem,
  getMultiScopeTranslation,
  handleInvalidateList,
  MultiScopeCompositionItem,
  MultiScopePayload,
  MultiScopeTranslationPayload,
  updateMultiScope,
  updateMultiScopeComposition,
  updateMultiScopeTranslation,
} from "store/gameCenter/multiScope/slice";
import { handleApiError } from "helpers/handleApiError";
import { useFormErrors } from "helpers/useFormErrors";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import { Modal } from "components/Modal/Modal";
import Button from "components/buttons/Button";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import SwitchBtn from "components/buttons/SwitchBtn";
import { ImageUpload } from "components/imageUpload";
import ModalSkeleton, {
  ModalFooterSkeleton,
} from "components/global/Skeleton/ModalSkeleton/ModalSkeleton";
import MultiScopeItemsTab from "../tabs/multiScope/MultiScopeItemsTab";
import MultiScopeLocalesTab from "../tabs/multiScope/MultiScopeLocalesTab";
import GameCenterCommonModal from "../GameCenterCommonModal";
import styles from "../GameCenterCommonModal.module.scss";
import {
  getChangedFields,
  normalizeOptionalText,
} from "helpers/getChangedFields";

type Tab = "main" | "items" | "locales";
const initialPayload: MultiScopePayload = {
  title: "",
  slug: "",
  isActive: true,
  image: null,
  icon: null,
};

const isLocalesTabEnabled = true;

const MultiScopeModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const id = Number(params.get("entityId")) || undefined;
  const [payload, setPayload] = useState<MultiScopePayload>(initialPayload);
  const [activeTab, setActiveTab] = useState<Tab>("main");
  const [loading, setLoading] = useState(Boolean(id));
  const [saving, setSaving] = useState(false);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemsContentLoading, setItemsContentLoading] = useState(false);
  const [itemsFetched, setItemsFetched] = useState(false);
  const [items, setItems] = useState<MultiScopeCompositionItem[]>([]);
  const [nestedEditor, setNestedEditor] = useState<{
    id: number;
    type: GameCenterFormTypes.tag | GameCenterFormTypes.collection;
  } | null>(null);
  const locales = useAppSelector((store) => store.settings.locales);
  const [activeLocale, setActiveLocale] = useState("");
  const [translations, setTranslations] = useState<
    Record<string, MultiScopeTranslationPayload>
  >({});
  const [initialTranslations, setInitialTranslations] = useState<
    Record<string, MultiScopeTranslationPayload>
  >({});
  const [localesLoading, setLocalesLoading] = useState(false);
  const formErrors = useFormErrors();

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    dispatch(getMultiScopeItem(id))
      .unwrap()
      .then((data) => {
        setPayload({
          ...initialPayload,
          ...data,
          image: data.image ?? null,
          icon: data.icon ?? null,
        });
      })
      .catch((error) => handleApiError({ error, t, dispatch, formErrors }))
      .finally(() => setLoading(false));
  }, [dispatch, id]);

  useEffect(() => {
    setItems([]);
    setItemsFetched(false);
  }, [id]);

  useEffect(() => {
    if (!activeLocale && locales.length) setActiveLocale(locales[0]);
  }, [activeLocale, locales]);

  const loadTranslation = (locale: string) => {
    if (!id || translations[locale]) return;
    setLocalesLoading(true);
    dispatch(getMultiScopeTranslation({ id, locale }))
      .unwrap()
      .then((translation) => {
        const data = translation ?? { locale, title: "", isActive: false };
        setTranslations((current) => ({ ...current, [locale]: data }));
        setInitialTranslations((current) => ({ ...current, [locale]: data }));
      })
      .catch((error) => handleApiError({ error, t, dispatch, formErrors }))
      .finally(() => setLocalesLoading(false));
  };

  useEffect(() => {
    if (activeTab === "locales" && activeLocale) loadTranslation(activeLocale);
  }, [activeTab, activeLocale, id]);

  const close = () => {
    navigate(location.pathname);
    dispatch(closeModal());
  };
  const change = (values: Partial<MultiScopePayload>) => {
    setPayload((current) => ({ ...current, ...values }));
    Object.keys(values).forEach(formErrors.clearFieldError);
  };
  const selectTab = (tab: Tab) => {
    if (!id && tab !== "main") {
      dispatch(
        showNotification({
          title: t("warning"),
          message: t("createEntityFirst"),
          type: NotificationTypes.warning,
        }),
      );
      return;
    }
    if (tab === "items" && id && !itemsFetched) {
      setItemsLoading(true);
      setItemsContentLoading(true);
      dispatch(getMultiScopeComposition(id))
        .unwrap()
        .then((data) => {
          setItems(data);
          setItemsFetched(true);
        })
        .catch((error) => handleApiError({ error, t, dispatch, formErrors }))
        .finally(() => setItemsLoading(false));
    }
    setActiveTab(tab);
  };
  const selectLocale = (locale: string) => {
    if (locale === activeLocale) return;
    setActiveLocale(locale);
  };
  const changeTranslation = (values: Partial<MultiScopeTranslationPayload>) => {
    if (!activeLocale) return;
    setTranslations((current) => ({
      ...current,
      [activeLocale]: {
        ...(current[activeLocale] ?? {
          locale: activeLocale,
          title: "",
          isActive: false,
        }),
        ...values,
        locale: activeLocale,
      },
    }));
    Object.keys(values).forEach(formErrors.clearFieldError);
  };
  const save = async () => {
    formErrors.resetErrors();
    setSaving(true);
    try {
      if (activeTab === "items" && id) {
        await dispatch(updateMultiScopeComposition({ id, items })).unwrap();
        const updatedItems = await dispatch(
          getMultiScopeComposition(id),
        ).unwrap();
        setItems(updatedItems);
        setItemsFetched(true);
        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityUpdateSuccess"),
            type: NotificationTypes.success,
          }),
        );
        return;
      }
      if (activeTab === "locales" && id && activeLocale) {
        const translation = translations[activeLocale] ?? {
          locale: activeLocale,
          title: "",
          isActive: false,
        };
        const changes = getChangedFields(
          initialTranslations[activeLocale],
          translation,
          ["title", "isActive"],
          { title: normalizeOptionalText, isActive: Boolean },
        );
        await dispatch(
          updateMultiScopeTranslation({
            id,
            payload: { locale: activeLocale, ...changes },
          }),
        ).unwrap();
        setTranslations((current) => ({
          ...current,
          [activeLocale]: translation,
        }));
        setInitialTranslations((current) => ({
          ...current,
          [activeLocale]: translation,
        }));
        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityUpdateSuccess"),
            type: NotificationTypes.success,
          }),
        );
        return;
      }
      if (id) {
        await dispatch(updateMultiScope({ ...payload, id })).unwrap();
        const updated = await dispatch(getMultiScopeItem(id)).unwrap();
        setPayload({
          ...initialPayload,
          ...updated,
          image: updated.image ?? null,
          icon: updated.icon ?? null,
        });
      } else {
        const newId = await dispatch(createMultiScope(payload)).unwrap();
        navigate(
          `${location.pathname}?activeModal=multiScope&entityId=${newId}`,
        );
      }
      if (!id) dispatch(handleInvalidateList());
      dispatch(
        showNotification({
          title: t("success"),
          message: t(id ? "entityUpdateSuccess" : "entityCreateSuccess"),
          type: NotificationTypes.success,
        }),
      );
    } catch (error) {
      handleApiError({ error, t, dispatch, formErrors });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Modal onClose={close}>
        <Modal.Header>{t("multiScope")}</Modal.Header>
        {loading ? (
          <ModalSkeleton />
        ) : (
          <>
            <Modal.Tabs
              tabs={
                (isLocalesTabEnabled
                  ? ["main", "items", "locales"]
                  : ["main", "items"]) as Tab[]
              }
              activeTab={activeTab}
              onClick={selectTab}
            />
            <Modal.Content className={styles.gameCenterContentWrapper}>
              {activeTab === "main" ? (
                <div className="game-form-wrapper">
                  <div className="game-form-content">
                    <div className="game-form-box">
                      <div className="game-form-box--item">
                        <InnerInput
                          id="multi-scope-title"
                          label={t("title")}
                          type="text"
                          value={payload.title}
                          onChange={(title) => change({ title })}
                          hasError={Boolean(formErrors.errors.title)}
                        />
                      </div>
                      <div className="game-form-box--item">
                        <InnerInput
                          id="multi-scope-slug"
                          label={t("slug")}
                          type="text"
                          value={payload.slug}
                          onChange={(slug) => change({ slug })}
                          hasError={Boolean(formErrors.errors.slug)}
                        />
                      </div>
                    </div>
                    <div className="game-form-box">
                      <div className="game-form-img game-form-img--game center">
                        <div className="game-form-field">
                          <div className="game-form-img-label">Image</div>
                          <div className="game-item">
                            <ImageUpload
                              image={payload.image ?? undefined}
                              isActive={payload.isActive}
                              size="large"
                              onAddImage={(image) =>
                                change({ image, removeImage: false })
                              }
                            />
                          </div>
                        </div>
                        <div className="game-form-controls">
                          <div className="game-form-switcher">
                            <SwitchBtn
                              activeTxt={t("active")}
                              notActiveTxt={t("hidden")}
                              isActive={payload.isActive}
                              handleClick={() =>
                                change({ isActive: !payload.isActive })
                              }
                              hasError={Boolean(formErrors.errors.isActive)}
                            />
                          </div>
                          <div className="game-form-icon">
                            <div className="game-form-img-label">Icon</div>
                            <ImageUpload
                              image={payload.icon ?? undefined}
                              isActive={payload.isActive}
                              type="square"
                              onAddImage={(icon) =>
                                change({ icon, removeIcon: false })
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : activeTab === "items" ? (
                <MultiScopeItemsTab
                  items={items}
                  onChange={setItems}
                  onEdit={(item) =>
                    setNestedEditor({
                      id: item.instanceId,
                      type:
                        item.instanceType === "tag"
                          ? GameCenterFormTypes.tag
                          : GameCenterFormTypes.collection,
                    })
                  }
                  selectedLoading={itemsLoading}
                  onLoadingChange={setItemsContentLoading}
                />
              ) : (
                <MultiScopeLocalesTab
                  locales={locales}
                  activeLocale={activeLocale}
                  translation={
                    activeLocale ? translations[activeLocale] : undefined
                  }
                  loading={localesLoading}
                  onLocaleChange={selectLocale}
                  onChange={changeTranslation}
                  fieldErrors={formErrors.errors}
                />
              )}
            </Modal.Content>
            <Modal.Footer>
              {(activeTab === "items" && itemsContentLoading) ||
              (activeTab === "locales" && localesLoading) ? (
                <ModalFooterSkeleton />
              ) : (
                <>
                  <Button isSecondary txt={t("cancel")} handleClick={close} />
                  <Button
                    isPrimary
                    txt={t("save")}
                    handleClick={save}
                    isLoad={saving}
                    disabled={
                      saving || (activeTab === "locales" && !activeLocale)
                    }
                  />
                </>
              )}
            </Modal.Footer>
          </>
        )}
      </Modal>
      {nestedEditor && (
        <GameCenterCommonModal
          entityIdOverride={String(nestedEditor.id)}
          modalTypeOverride={nestedEditor.type}
          onCloseOverride={() => setNestedEditor(null)}
        />
      )}
    </>
  );
};

export default MultiScopeModal;
